import {
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { useUserContext } from "@/context/user.context";
import {
  bulkHighlightScreenerSession,
  bulkSetSessionViewedStatus,
  getCandidateScreenings,
  getScreeningsByLocation,
  highlightScreenerSession,
  markSessionViewed,
  reprocessScreenerSession,
} from "@/app/api/screeners";
import { SCREENER_LOCATION_QUERY_KEYS } from "@/hooks/queries/screener-locations.queries";
import { useAuthedQuery } from "@/hooks/queries/use-authed-query.hook";
import type { CandidateResponse, TableScreenings } from "@/types/screeners";
import type { Nullable } from "@/types/utils";
import { FEATURE_FLAGS, useFeatureIsOn } from "@/utils/growthbook";

const SCREENINGS_QUERY_KEY_BASE = "screening";
export const SCREENINGS_QUERY_KEYS = {
  base: [SCREENINGS_QUERY_KEY_BASE] as const,
  candidate: (email: string, sessionId?: string) => [
    ...SCREENINGS_QUERY_KEYS.base,
    "candidate",
    email,
    sessionId,
  ],
  list: (locationId: string, filters: object) =>
    [...SCREENINGS_QUERY_KEYS.base, locationId, filters] as const,
};

interface FilterParamsQueryable {
  toString(): string;
  toObject(): object;
}

export const useScreeningsByLocationQuery = (
  screenerLocationId: string,
  filterParams: FilterParamsQueryable,
  config: Partial<UseQueryOptions<TableScreenings>> = {},
) => {
  return useAuthedQuery<TableScreenings>({
    placeholderData: (previousData) => previousData,
    queryFn: () =>
      getScreeningsByLocation(screenerLocationId, filterParams.toString()),
    queryKey: SCREENINGS_QUERY_KEYS.list(
      screenerLocationId,
      filterParams.toObject(),
    ),
    ...config,
  });
};

export const useCandidateScreeningsQuery = (
  email = "",
  sessionId = "",
  config: Partial<UseQueryOptions<CandidateResponse>> = {},
) => {
  return useAuthedQuery<CandidateResponse>({
    queryFn: () => getCandidateScreenings(email, sessionId),
    queryKey: SCREENINGS_QUERY_KEYS.candidate(email, sessionId),
    ...config,
    enabled: !!email && config.enabled,
  });
};

export const useHighlightScreenerSessionQuery = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: Nullable<string> }) =>
      highlightScreenerSession(id, value),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: SCREENINGS_QUERY_KEYS.base }),
  });
};

export const useBulkHighlightQuery = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionIds,
      color,
    }: {
      sessionIds: string[];
      color: Nullable<string>;
    }) => bulkHighlightScreenerSession(sessionIds, color),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: SCREENINGS_QUERY_KEYS.base }),
  });
};

export const useBulkSetViewedStatusQuery = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionIds,
      viewedAt,
    }: {
      sessionIds: string[];
      viewedAt: Nullable<string>;
    }) => bulkSetSessionViewedStatus(sessionIds, viewedAt),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: SCREENINGS_QUERY_KEYS.base });
    },
  });
};

export const useReprocessScreenerSessionQuery = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => reprocessScreenerSession(sessionId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: SCREENINGS_QUERY_KEYS.base });
    },
  });
};

export const useMarkSessionViewedQuery = () => {
  const client = useQueryClient();
  const { user } = useUserContext();
  const shouldMarkInternalSessionsViewed = useFeatureIsOn(
    FEATURE_FLAGS.internalMarkAsViewedHack,
  );

  return useMutation({
    mutationFn: (sessionId: string) => {
      // Preserve the internal-user no-op unless a targeted CSR needs normal viewed tracking.
      if (user?.isInternal && !shouldMarkInternalSessionsViewed) {
        return Promise.resolve();
      }

      return markSessionViewed(sessionId);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: SCREENINGS_QUERY_KEYS.base });
      // Invalidate locations as well so the table view updates unviewed count
      client.invalidateQueries({ queryKey: SCREENER_LOCATION_QUERY_KEYS.base });
    },
  });
};
